'use client';

// Package Imports
import React, { useEffect, ReactNode } from 'react';
import { useSession, getSession, signOut } from 'next-auth/react';
import { usePathname } from 'next/navigation';

// Style Imports
import styles from './accountWrapper.module.scss';

// Component Imports
import Breadcrumb from '@/components/breadcrumbs';
import Loader from '@/components/loader';
import AccountLogin from '@/components/accountLogin';
import AccountHeader from '@/components/accountHeader';
import AccountRegistration from '@/components/accountRegistration';

/**
 * AccountWrapper Component
 * 
 * This component wraps content for authenticated users. If the user is logged in, it displays
 * the child components passed to it. If not, it shows the login and registration options.
 * 
 * @param {React.ReactNode} children - The child components to render when authenticated.
 * @param {string} title - The title text to display as an `h1`.
 * @param {Array<{ label: string, href: string }>} breadcrumbItems - Array of breadcrumb items for navigation.
 * @returns {JSX.Element} The account wrapper component.
 */
interface AccountWrapperProps {
  children: ReactNode;
  title: string;
  breadcrumbItems: { label: string; href: string }[];
}

const AccountWrapper: React.FC<AccountWrapperProps> = ({ children, title, breadcrumbItems }) => {
  const { data: session, status } = useSession();
  const pathname = usePathname();

  const isLoggedIn = session && session.user;

  useEffect(() => {
    const refreshSession = async () => {
      await getSession(); // Forces session update when page is mounted
    };
    refreshSession();
  }, [status, pathname]);

  useEffect(() => {
    const signOutUser = async () => {
      if (pathname !== '/my-account/') {
        await signOut({ callbackUrl: '/my-account/' });
      }
    }; 
    if (status === 'unauthenticated' && pathname !== '/my-account/') {
      signOutUser();
    }
  }, [status, pathname]);

  if (!status || status === 'loading' || status === 'unauthenticated') {
    if (status === 'loading') {
      return <Loader variant="2" />;
    }
    if (status === 'unauthenticated' && pathname !== '/my-account/') {
      return <Loader variant="2" />;
    }
  }

  return (
    <section className={styles.wrapper}>
      <Breadcrumb items={breadcrumbItems} />
      <h1>{title}</h1>
      {isLoggedIn ? (
        <>
          <AccountHeader />
          <>{children}</>
        </>
      ) : (        
        <div className={styles.inner}>
          <div className={styles.columns}>
            <div className={styles.login}>
              <h2>Login</h2>
              <AccountLogin />
            </div>
            <div className={styles.register}>
              <h2>Register</h2>
              <AccountRegistration />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AccountWrapper;
